import { useState, useEffect } from 'react';
import { 
  fetchCategories, 
  fetchProducts, 
  fetchProductBySlug, 
  fetchProductImages,
  fetchFeaturedProducts
} from '@/lib/supabase';
import { 
  categories as mockCategories, 
  products as mockProducts,
  getProductsByCategory,
  getProductBySlug as getMockProductBySlug,
  getProductImages as getMockProductImages,
  getCategoryBySlug as getMockCategoryBySlug
} from '@/data/products';

// Hook for fetching categories
export function useCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, error } = await fetchCategories();
      
      if (error || !data) {
        // Fallback to mock data
        console.log('Using mock categories data');
        setCategories(mockCategories);
      } else {
        setCategories(data);
      }
      
      setError(error);
      setLoading(false);
    }
    
    load();
  }, []);

  return { categories, loading, error };
}

// Hook for fetching products
export function useProducts(categorySlug = null) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, error } = await fetchProducts(categorySlug);
      
      if (error || !data) {
        // Fallback to mock data
        console.log('Using mock products data');
        setProducts(getProductsByCategory(categorySlug));
      } else {
        setProducts(data);
      }
      
      setError(error);
      setLoading(false);
    }
    
    load();
  }, [categorySlug]);

  return { products, loading, error };
}

// Hook for fetching single product
export function useProduct(slug) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      if (!slug) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      const { data, error } = await fetchProductBySlug(slug);
      
      if (error || !data) {
        // Fallback to mock data
        console.log('Using mock product data');
        setProduct(getMockProductBySlug(slug));
      } else {
        setProduct(data);
      }
      
      setError(error);
      setLoading(false);
    }
    
    load();
  }, [slug]);

  return { product, loading, error };
}

// Hook for fetching product images
export function useProductImages(productId) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      if (!productId) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      const { data, error } = await fetchProductImages(productId);
      
      if (error || !data || data.length === 0) {
        // Fallback to mock data
        console.log('Using mock product images');
        setImages(getMockProductImages(productId));
      } else {
        setImages(data);
      }
      
      setError(error);
      setLoading(false);
    }
    
    load();
  }, [productId]);

  return { images, loading, error };
}


// Hook for fetching featured products by IDs
export function useFeaturedProducts(productIds) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      if (!productIds || productIds.length === 0) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      const { data, error } = await fetchFeaturedProducts(productIds);
      
      if (error || !data) {
        console.log('Failed to fetch featured products');
        setProducts([]);
      } else {
        setProducts(data);
      }
      
      setError(error);
      setLoading(false);
    }
    
    load();
  }, [JSON.stringify(productIds)]);

  return { products, loading, error };
}
